import BaseModal from "@/components/shared/BaseModal";
import { Box } from "@/components/ui/box";
import { Input, InputField } from "@/components/ui/input";
import { Text } from "@/components/ui/text";
import { validateLicensePlate } from "@/utils/licensePlateValidation";
import { useT } from "@transifex/react";
import { useState } from "react";

interface UpdatePlateModalProps {
  isOpen: boolean;
  onClose: () => void;
  licensePlate: string;
  onConfirm: (newPlate: string) => void;
  loading: boolean;
}

const UpdatePlateModal: React.FC<UpdatePlateModalProps> = ({ isOpen, onClose, licensePlate, onConfirm, loading }) => {
  const [newPlate, setNewPlate] = useState<string>(licensePlate);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const t = useT();

  const isSamePlate = newPlate === licensePlate;

  const handleSelectPlate = (value: string) => {
    const formattedPlate = value
      .trim()
      .replace(/[^a-z0-9]/gi, "")
      .toLocaleUpperCase()
      .slice(0, 12);
    const errorMessage = validateLicensePlate(formattedPlate);
    setErrorMessage(errorMessage ? t(errorMessage) : null);
    setNewPlate(formattedPlate);
  };

  const handleConfirm = () => {
    if (!isSamePlate) {
      onConfirm(newPlate);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={t("Update Plate")}
      subtitle={t("Did you check out with the wrong license plate? No worries you can still update it.")}
      buttons={[{ title: "Update License Plate", onPress: handleConfirm, loading: loading, disabled: isSamePlate || !!errorMessage }]}
      noIcon={true}
    >
      <Box className="gap-[10px]">
        <Text className="text-typography-900 text-[14px] font-[600] leading-[20px] align-middle">{t("License Plate No.")}</Text>
        <Box className="w-full">
          <Input
            className={`h-[52px] pt-[8px] pb-[8px] pl-[8px] gap-[8px] rounded-[6px] border-[1px] ${errorMessage ? "bg-error-50 border-error-400" : "bg-background-50 border-outline-300"}`}
          >
            <InputField
              className="text-typography-900 text-[16px] font-[400] leading-[22px] align-middle placeholder-text-typography-900 placeholder-opacity-50"
              data-test-id="vehicleInputField"
              placeholder={t("ABC123")}
              value={newPlate}
              onChangeText={handleSelectPlate}
              autoFocus
              autoCapitalize="characters"
              maxLength={12}
            />
          </Input>
          {errorMessage && <Text className="text-error-100 text-[14px] leading-[20px] pt-1">{errorMessage}</Text>}
        </Box>
      </Box>
    </BaseModal>
  );
};

export default UpdatePlateModal;
